import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconBuildingStore,
  IconHome, IconBox, IconUser, IconBook, IconMail, IconFileText,
  IconShoppingBag, IconTruck, IconTag,
  IconPalette, IconSettings,
  IconMailForward, IconMenu2, IconFile, IconLogout, IconAlertTriangle, IconInbox, IconUsers,
} from '@tabler/icons-react';
import AdminLogin, { AdminAuthCard } from './AdminLogin';
import { useAdminSession, signOut } from '../lib/auth';
import AdminHomepage from './AdminHomepage';
import AdminProducts from './AdminProducts';
import AdminPages from './AdminPages';
import AdminAbout from './AdminAbout';
import AdminCareGuide from './AdminCareGuide';
import AdminContact from './AdminContact';
import AdminPolicies from './AdminPolicies';
import AdminOrders from './AdminOrders';
import AdminShipping from './AdminShipping';
import AdminPromos from './AdminPromos';
import AdminEmails from './AdminEmails';
import AdminMessages from './AdminMessages';
import AdminSubscribers from './AdminSubscribers';
import AdminColours from './AdminColours';
import AdminStoreStatus from './AdminStoreStatus';
import ToastContainer, { showToast } from '../components/ui/Toast';
import './AdminLayout.css';

var navSections = [
  { label: 'Website content', items: [
    { id: 'homepage', icon: IconHome, label: 'Homepage text' },
    { id: 'products', icon: IconBox, label: 'Products' },
    { id: 'pages', icon: IconFile, label: 'Pages' },
    { id: 'about', icon: IconUser, label: 'About page' },
    { id: 'care', icon: IconBook, label: 'Care guide' },
    { id: 'contact', icon: IconMail, label: 'Contact page' },
    { id: 'policies', icon: IconFileText, label: 'Policies' },
  ]},
  { label: 'Store', items: [
    { id: 'orders', icon: IconShoppingBag, label: 'Orders' },
    { id: 'shipping', icon: IconTruck, label: 'Shipping' },
    { id: 'promos', icon: IconTag, label: 'Promo codes' },
  ]},
  { label: 'Communications', items: [
    { id: 'messages', icon: IconInbox, label: 'Messages' },
    { id: 'subscribers', icon: IconUsers, label: 'Subscribers' },
    { id: 'emails', icon: IconMailForward, label: 'Email templates' },
  ]},
  { label: 'Settings', items: [
    { id: 'colours', icon: IconPalette, label: 'Brand colours' },
    { id: 'status', icon: IconSettings, label: 'Store status' },
  ]},
];

var panels = {
  homepage: AdminHomepage,
  products: AdminProducts,
  pages: AdminPages,
  about: AdminAbout,
  care: AdminCareGuide,
  contact: AdminContact,
  policies: AdminPolicies,
  orders: AdminOrders,
  shipping: AdminShipping,
  promos: AdminPromos,
  messages: AdminMessages,
  subscribers: AdminSubscribers,
  emails: AdminEmails,
  colours: AdminColours,
  status: AdminStoreStatus,
};

export default function AdminLayout() {
  var _active = useState('homepage');
  var active = _active[0];
  var setActive = _active[1];
  var _sideOpen = useState(false);
  var sideOpen = _sideOpen[0];
  var setSideOpen = _sideOpen[1];
  var _signingOut = useState(false);
  var signingOut = _signingOut[0];
  var setSigningOut = _signingOut[1];
  var auth = useAdminSession();
  var Panel = panels[active] || AdminHomepage;

  function nav(id) {
    setActive(id);
    setSideOpen(false);
  }

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    var res = await signOut();
    setSigningOut(false);
    if (res.error) showToast(res.error, 'error');
  }

  if (auth.loading) {
    return (
      <div className="al-page"><p className="al-wait">{'Checking your sign-in…'}</p></div>
    );
  }

  if (!auth.session) {
    return <AdminLogin />;
  }

  if (!auth.isAdmin) {
    return (
      <AdminAuthCard pageTitle="Not an admin" heading="No admin access" icon={<IconAlertTriangle size={22} />}
        intro={'You’re signed in as ' + ((auth.session.user && auth.session.user.email) || 'this account') +
          ', but it isn’t set up as a shop admin. Sign out and try a different account, or ask whoever set up the site to add you.'}>
        <button type="button" className="al-submit" onClick={handleSignOut} disabled={signingOut}>
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </AdminAuthCard>
    );
  }

  return (
    <div className="adm">
      {sideOpen && <div className="adm-overlay" onClick={function() { setSideOpen(false); }} />}
      <aside className={'adm-side' + (sideOpen ? ' adm-side--open' : '')}>
        <div className="adm-brand">
          <div className="adm-brand-name">Andre <span>M</span> Leather</div>
          <div className="adm-brand-sub">Admin panel</div>
        </div>
        {navSections.map(function(section, si) {
          return (
            <div key={si}>
              <div className="adm-nav-label">{section.label}</div>
              {section.items.map(function(item) {
                return (
                  <button
                    key={item.id}
                    className={'adm-nav-btn' + (active === item.id ? ' adm-nav-btn--active' : '')}
                    onClick={function() { nav(item.id); }}
                  >
                    <item.icon size={15} /> {item.label}
                  </button>
                );
              })}
            </div>
          );
        })}
        <div className="adm-account">
          {auth.session.user && auth.session.user.email && (
            <div className="adm-account-email" title={auth.session.user.email}>{auth.session.user.email}</div>
          )}
          <Link to="/" className="adm-nav-btn adm-viewshop-btn">
            <IconBuildingStore size={15} /> View shop
          </Link>
          <button className="adm-nav-btn adm-signout-btn" onClick={handleSignOut} disabled={signingOut}>
            <IconLogout size={15} /> {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </aside>
      <main className="adm-main">
        <button className="adm-menu-btn" onClick={function() { setSideOpen(true); }}>
          <IconMenu2 size={18} /> Menu
        </button>
        <Panel />
      </main>
      <ToastContainer />
    </div>
  );
}